import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Checkbox, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import api from 'api';
import type {
    ChecklistDocumentItem,
    CreateChecklistDocument,
    CreateChecklistResponse,
} from 'api/rest/document_checklist';
import { RootState, AppDispatch } from 'redux/store';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName, countryNameOptions } from 'utils/countries';
import {
    Actions,
    Form,
    ModalNotification,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';
import {
    formAreaSx,
    longOptionsMenuProps,
} from 'views/Dashboards-B2B/components/eligibility/Eligibility.styled';
import { InfoIcon } from 'views/Dashboards-B2B/components/Table.styled';
import * as S from './AddDocumentPopoverMenu.styled';

interface ProgramOption {
    value: string;
    label: string;
    display_color?: string;
}

/** Selection key: template id when present, else the canonical (English) name. */
const getKey = (item: ChecklistDocumentItem): string =>
    item.document_template_id != null
        ? `t:${item.document_template_id}`
        : `n:${item.document_name}`;

const AddDocumentPopoverMenu = ({ closeMenu }: { closeMenu: () => void }) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    const { codes } = useEnabledCountries();
    const countryOptions = useMemo(
        () => countryNameOptions(codes, activeApp?.country),
        [activeApp?.country, codes]
    );
    const initialCountry = chosenCountryName(codes, activeApp?.country) ?? '';

    const appId = activeApp?.id;

    const [country, setCountry] = useState(initialCountry);

    useEffect(() => {
        setCountry((current) => current || initialCountry);
    }, [initialCountry]);
    const [service, setService] = useState('');
    const [programs, setPrograms] = useState<ProgramOption[]>([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);

    const [checklist, setChecklist] = useState<ChecklistDocumentItem[]>([]);
    const [loadingChecklist, setLoadingChecklist] = useState(false);
    const [checklistError, setChecklistError] = useState<string | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [result, setResult] = useState<CreateChecklistResponse | null>(null);

    useEffect(() => {
        if (!country) {
            setPrograms([]);
            setService('');
            return;
        }

        let cancelled = false;
        const loadPrograms = async () => {
            setLoadingPrograms(true);
            try {
                const resp = await api.programList.get(undefined, country);
                if (cancelled) return;
                if (resp?.status !== 200 && resp?.status !== 201) {
                    setPrograms([]);
                    return;
                }

                const data = resp?.data;
                if (!Array.isArray(data) || data.length === 0) {
                    setPrograms([]);
                    return;
                }

                if (typeof data[0] === 'object' && data[0].program_name) {
                    setPrograms(
                        data.map((e: { program_name: string; display_color?: string }) => ({
                            value: e.program_name,
                            label: e.program_name,
                            display_color: e.display_color,
                        }))
                    );
                } else {
                    setPrograms(
                        data.map((programName: string) => ({
                            value: programName,
                            label: programName,
                        }))
                    );
                }
            } catch (err) {
                if (!cancelled) setPrograms([]);
                console.error(err);
            } finally {
                if (!cancelled) setLoadingPrograms(false);
            }
        };

        loadPrograms();
        return () => {
            cancelled = true;
        };
    }, [country]);

    // Preview the checklist for the selected country + service. Selectable rows
    // (those not already attached) are pre-checked so accepting the suggested
    // set is one click; `already_exists` rows are shown as added and locked.
    useEffect(() => {
        if (!appId || !country || !service) {
            setChecklist([]);
            setSelectedKeys([]);
            setChecklistError(null);
            return;
        }

        let cancelled = false;
        const loadChecklist = async () => {
            setLoadingChecklist(true);
            setChecklistError(null);
            try {
                const resp = await api.documentChecklist.get(appId, country, service);
                if (cancelled) return;
                const items = resp?.data?.document_checklist ?? [];
                setChecklist(items);
                setSelectedKeys(items.filter((item) => !item.already_exists).map(getKey));
            } catch (err) {
                if (!cancelled) {
                    setChecklist([]);
                    setSelectedKeys([]);
                    setChecklistError(
                        t('checklistLoadError', {
                            lng: language,
                            defaultValue:
                                'We couldn’t load the document checklist for this service. Please try again.',
                        })
                    );
                }
                console.error(err);
            } finally {
                if (!cancelled) setLoadingChecklist(false);
            }
        };

        loadChecklist();
        return () => {
            cancelled = true;
        };
    }, [appId, country, service, language, t]);

    const serviceDisabled = !country || loadingPrograms || programs.length === 0;
    const serviceHelperText = !country
        ? t('selectCountryFirst', {
              lng: language,
              defaultValue: 'Select a country to see available services.',
          })
        : loadingPrograms
          ? t('loadingServices', { lng: language, defaultValue: 'Loading services…' })
          : programs.length === 0
            ? t('noServicesAvailable', {
                  lng: language,
                  defaultValue: 'No services available for this country.',
              })
            : ' ';

    const selectedProgram = useMemo(
        () => programs.find((option) => option.value === service) ?? null,
        [programs, service]
    );

    const selectableItems = useMemo(
        () => checklist.filter((item) => !item.already_exists),
        [checklist]
    );
    const allSelected =
        selectableItems.length > 0 && selectedKeys.length === selectableItems.length;

    const toggleDocument = (item: ChecklistDocumentItem) => {
        if (item.already_exists) return;
        const key = getKey(item);
        setSelectedKeys((prev) =>
            prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key]
        );
    };

    const toggleSelectAll = () => {
        setSelectedKeys(allSelected ? [] : selectableItems.map(getKey));
    };

    const canCreate = !!appId && !!country && !!service && selectedKeys.length > 0 && !creating;

    const handleCreateChecklist = async () => {
        if (!canCreate || !appId) return;
        const documents: CreateChecklistDocument[] = selectableItems
            .filter((item) => selectedKeys.includes(getKey(item)))
            .map((item) =>
                item.document_template_id != null
                    ? { document_template_id: item.document_template_id }
                    : { document_name: item.document_name }
            );

        setCreating(true);
        setCreateError(null);
        try {
            const resp = await api.documentChecklist.post(appId, {
                country,
                service_name: service,
                documents,
            });
            // Refresh the case's document list so the new rows show immediately.
            dispatch(loadDocs(appId.toString()));
            setResult(resp.data);
        } catch (err) {
            console.error(err);
            setCreateError(
                t('checklistCreateError', {
                    lng: language,
                    defaultValue:
                        'Something went wrong creating these documents. Please try again.',
                })
            );
        } finally {
            setCreating(false);
        }
    };

    if (result) {
        const message =
            result.created_count > 0
                ? t('documentsCreatedCount', {
                      lng: language,
                      count: result.created_count,
                      defaultValue: `Added ${result.created_count} document(s) to this case.`,
                  })
                : t('documentsAlreadyOnFile', {
                      lng: language,
                      defaultValue: 'All selected documents were already on file.',
                  });
        return (
            <Form
                style={{ marginTop: '16px' }}
                onSubmit={(event) => {
                    event.preventDefault();
                    closeMenu();
                }}
            >
                <Section>
                    <ModalNotification
                        variant="success"
                        title={t('documentsCreated', {
                            lng: language,
                            defaultValue: 'Documents added',
                        })}
                        message={
                            <>
                                {message}
                                {result.created_document_names.length > 0 && (
                                    <S.CreatedList>
                                        {result.created_document_names.map((name) => (
                                            <li key={name}>{name}</li>
                                        ))}
                                    </S.CreatedList>
                                )}
                            </>
                        }
                    />
                </Section>
                <Actions>
                    <PrimaryButton type="submit">
                        {t('done', { lng: language, defaultValue: 'Done' })}
                    </PrimaryButton>
                </Actions>
            </Form>
        );
    }

    return (
        <Form
            style={{ marginTop: '16px' }}
            onSubmit={(event) => {
                event.preventDefault();
                handleCreateChecklist();
            }}
        >
            <Section>
                <Box sx={formAreaSx}>
                    <Stack spacing={2}>
                        <TextField
                            select
                            required
                            fullWidth
                            size="small"
                            label={t('country', { lng: language, defaultValue: 'Country' })}
                            value={country}
                            onChange={(event) => {
                                const value = event.target.value;
                                setCountry(value);
                                setService('');
                            }}
                            SelectProps={{ MenuProps: longOptionsMenuProps }}
                        >
                            {countryOptions.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Autocomplete
                            fullWidth
                            size="small"
                            disabled={serviceDisabled}
                            options={programs}
                            value={selectedProgram}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            onChange={(_event, newValue) =>
                                setService(newValue ? newValue.value : '')
                            }
                            noOptionsText={t('noServicesAvailable', {
                                lng: language,
                                defaultValue: 'No services available for this country.',
                            })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label={t('service', {
                                        lng: language,
                                        defaultValue: 'Service',
                                    })}
                                    helperText={serviceHelperText}
                                />
                            )}
                        />
                    </Stack>
                </Box>
            </Section>

            {service && (
                <S.ChecklistCard>
                    <S.ChecklistHeader>
                        <span>
                            <S.ChecklistTitle>
                                {t('documents', { lng: language, defaultValue: 'Documents' })}
                            </S.ChecklistTitle>
                            {selectableItems.length > 0 && (
                                <S.ChecklistCount>
                                    {selectedKeys.length}/{selectableItems.length}
                                </S.ChecklistCount>
                            )}
                        </span>
                        <S.SelectAllButton
                            type="button"
                            disabled={loadingChecklist || selectableItems.length === 0}
                            onClick={toggleSelectAll}
                        >
                            {allSelected
                                ? t('deselectAll', {
                                      lng: language,
                                      defaultValue: 'Deselect all',
                                  })
                                : t('selectAll', { lng: language, defaultValue: 'Select all' })}
                        </S.SelectAllButton>
                    </S.ChecklistHeader>

                    {loadingChecklist ? (
                        <S.ChecklistMessage>
                            {t('loadingDocuments', {
                                lng: language,
                                defaultValue: 'Loading documents…',
                            })}
                        </S.ChecklistMessage>
                    ) : checklistError ? (
                        <S.ChecklistMessage>{checklistError}</S.ChecklistMessage>
                    ) : checklist.length === 0 ? (
                        <S.ChecklistMessage>
                            {t('noDocumentsForService', {
                                lng: language,
                                defaultValue: 'No documents available for this service.',
                            })}
                        </S.ChecklistMessage>
                    ) : (
                        <S.ChecklistList>
                            {checklist.map((item) => {
                                const key = getKey(item);
                                const checked = item.already_exists || selectedKeys.includes(key);
                                return (
                                    <S.ChecklistRow key={key}>
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={item.already_exists}
                                            onChange={() => toggleDocument(item)}
                                            sx={{
                                                color: 'rgba(131, 83, 226, 0.5)',
                                                '&.Mui-checked': { color: '#8353e2' },
                                            }}
                                            inputProps={{ 'aria-label': item.name }}
                                        />
                                        <S.DocNameWrap
                                            disabled={item.already_exists}
                                            onClick={() => toggleDocument(item)}
                                        >
                                            <S.DocName muted={item.already_exists}>
                                                {item.name}
                                            </S.DocName>
                                            {item.is_form && (
                                                <S.FormBadge>
                                                    {t('form', {
                                                        lng: language,
                                                        defaultValue: 'Form',
                                                    })}
                                                </S.FormBadge>
                                            )}
                                            {item.already_exists && (
                                                <S.AddedBadge>
                                                    {t('added', {
                                                        lng: language,
                                                        defaultValue: 'Added',
                                                    })}
                                                </S.AddedBadge>
                                            )}
                                        </S.DocNameWrap>
                                        <Tooltip
                                            arrow
                                            title={item.description || ''}
                                            placement="top"
                                        >
                                            <S.InfoWrap>
                                                <InfoIcon open={false} />
                                            </S.InfoWrap>
                                        </Tooltip>
                                    </S.ChecklistRow>
                                );
                            })}
                        </S.ChecklistList>
                    )}
                </S.ChecklistCard>
            )}

            {createError && (
                <ModalNotification
                    variant="error"
                    title={t('errorTitle', { lng: language, defaultValue: 'Oops!' })}
                    message={createError}
                />
            )}

            <Actions>
                <SecondaryButton type="button" onClick={closeMenu}>
                    {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={!canCreate}>
                    {creating
                        ? t('creating', { lng: language })
                        : t('createChecklist', {
                              lng: language,
                              defaultValue: 'Create documents',
                          })}
                </PrimaryButton>
            </Actions>
        </Form>
    );
};

export default AddDocumentPopoverMenu;
